// CAPABILITY MAP surface (N-FDE-3), the "Signal" re-layout (Capability Map v2) — "show a client exactly
// what the agent can and cannot do". The dashboard renders `agent.getCapabilityMap` (IFC-AGENT-010)
// DERIVED from the generated Capability registry (P-MAPPED, G-148): it cannot drift from code because it
// is derived from code. The screen restates the SAME projection three ways: the permission LEDGER (every
// capability sits in one of three boxes — gated / ungated write / read, in plain English), the AREA rail
// (per-owner roll-ups with their permission mix), and the grouped expandable rows (the registry's own
// operational summary, with the recorded flags restated as meta chips). Search, filters, grouping, and
// row expansion are client view-state; READ-ONLY on this surface — it routes no write.
//
// EXPORT (N-FDE-3 AC-3): an Operator can export, as a single read-only action, a scoped explanation
// artifact to share with a client. The export is a READ-ONLY COMPOSITION of the SAME projection the
// surface renders (composeScopedExplanation): it adds no new data, performs no write, and asserts no side
// effect (G-154). It exposes no credential value (G-140) and no internal/build/third-party identifier
// (G-197). The download side effect is injected so a UI test asserts the composed content without
// touching the blob/DOM layer. Tokens only (G-181).

import { useCallback, useState } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { ScreenToolbar, ScreenBody, TbStamp, TbInfo } from '../app/screen-chrome.tsx';
import { CapabilityLedger } from './components/CapabilityLedger.tsx';
import { CapabilityAreaRail } from './components/CapabilityAreaRail.tsx';
import { CapabilityGroups, type CapabilityGroupView } from './components/CapabilityGroups.tsx';
import {
  BUCKET_META,
  BUCKET_ORDER,
  areaAggregates,
  areaLabelOf,
  bucketCounts,
  bucketOf,
  searchCapabilities,
  type CapabilityBucket,
} from './components/capability-buckets.ts';
import { composeScopedExplanation } from './capability-export.ts';
import type { ScopedExplanationArtifact } from './capability-export.ts';
import type { CapabilityEntryView, CapabilityMapProjection } from './operate-projections.ts';

/** The default export sink — a browser download of the composed artifact (guarded for a non-browser test). */
function downloadArtifact(artifact: ScopedExplanationArtifact): void {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return;
  const blob = new Blob([artifact.body], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = artifact.filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export interface CapabilityMapSurfaceProps {
  /** The export sink (injected in a test to assert the composed artifact); defaults to a browser download. */
  readonly onExport?: (artifact: ScopedExplanationArtifact) => void;
}

/** The Capability map surface. */
export function CapabilityMapSurface({
  onExport = downloadArtifact,
}: CapabilityMapSurfaceProps = {}): React.JSX.Element {
  const api = useSurfaceApi();
  const state = useProjection<CapabilityMapProjection>(api.getCapabilityMap);
  return (
    <>
      <ScreenToolbar title="Capability map">
        {state.data !== null ? (
          <TbStamp>{state.data.capabilities.length} capabilities · read from the code</TbStamp>
        ) : null}
        <TbInfo
          title="Capability map"
          tip="Every route and tool the agent can reach — derived from the generated registry, so it cannot drift from code. Read-only."
        />
      </ScreenToolbar>
      <ScreenBody>
        <SurfaceFrame state={state}>
          {(view) => <CapabilityMapBody view={view} onExport={onExport} />}
        </SurfaceFrame>
      </ScreenBody>
    </>
  );
}

/** Build the pane's group cards for the chosen mode over the filtered pool. */
function buildGroups(
  pool: readonly CapabilityEntryView[],
  mode: 'permission' | 'area',
): readonly CapabilityGroupView[] {
  if (mode === 'permission') {
    return BUCKET_ORDER.map((key) => ({
      key,
      label: BUCKET_META[key].label,
      bucket: key,
      note: BUCKET_META[key].note,
      items: pool.filter((cap) => bucketOf(cap) === key),
    })).filter((group) => group.items.length > 0);
  }
  return areaAggregates(pool).map((area) => ({
    key: area.key,
    label: area.label,
    bucket: null,
    note: null,
    items: pool.filter((cap) => cap.owner === area.key),
  }));
}

/** The pane's honest title/note/empty copy for the current search + filters. */
function paneCopy(args: {
  readonly searching: boolean;
  readonly query: string;
  readonly shown: number;
  readonly area: string;
  readonly bucket: CapabilityBucket | 'all';
}): { readonly title: string; readonly note: string; readonly emptyTitle: string } {
  const { searching, query, shown, area, bucket } = args;
  if (searching) {
    return {
      title: 'Search results',
      note: `${shown} ${shown === 1 ? 'capability matches' : 'capabilities match'} “${query}”, across every area`,
      emptyTitle: 'Nothing matches that',
    };
  }
  const lens = {
    ask: 'only what needs an approval first',
    act: 'only what runs without one',
    look: 'only what Ritona can read',
  } as const;
  const base =
    area === 'all'
      ? 'Everything Ritona can reach, grouped by what it needs from you'
      : `Everything reachable in ${areaLabelOf(area)}`;
  return {
    title: area === 'all' ? 'All areas' : areaLabelOf(area),
    note: bucket === 'all' ? base : `${base} · ${lens[bucket]}`,
    emptyTitle: 'Nothing here matches the filter',
  };
}

/** All client view-state + the derived pools (search → bucket → area; the rail tracks the bucket). */
function useCapabilityFilters(caps: readonly CapabilityEntryView[]) {
  const [query, setQueryState] = useState('');
  const [bucket, setBucketState] = useState<CapabilityBucket | 'all'>('all');
  const [area, setAreaState] = useState('all');
  const [mode, setMode] = useState<'permission' | 'area'>('permission');
  const [openId, setOpenId] = useState<string | null>(null);
  const [more, setMore] = useState<Readonly<Record<string, boolean>>>({});
  const searching = query.trim().length > 0;
  const searched = searchCapabilities(caps, query);
  const bucketed = bucket === 'all' ? searched : searched.filter((cap) => bucketOf(cap) === bucket);
  const pool =
    area === 'all' || searching ? bucketed : bucketed.filter((cap) => cap.owner === area);
  const railPool = bucket === 'all' ? caps : caps.filter((cap) => bucketOf(cap) === bucket);
  return {
    query,
    bucket,
    area,
    mode,
    openId,
    more,
    searching,
    pool,
    railPool,
    setMode,
    setQuery: (value: string): void => {
      setQueryState(value);
      setOpenId(null);
    },
    setBucket: (value: CapabilityBucket | 'all'): void => {
      setBucketState(value);
      setOpenId(null);
    },
    setArea: (value: string): void => {
      setAreaState(value);
      setOpenId(null);
    },
    toggleRow: (id: string): void => setOpenId((current) => (current === id ? null : id)),
    toggleMore: (key: string): void =>
      setMore((current) => ({ ...current, [key]: current[key] !== true })),
  };
}

/** The header row: the capability search + the read-only scoped-explanation export. */
function CapabilityMapHeader({
  query,
  onQuery,
  onExportClick,
}: {
  readonly query: string;
  readonly onQuery: (value: string) => void;
  readonly onExportClick: () => void;
}): React.JSX.Element {
  return (
    <div className="cm-head">
      <input
        type="search"
        className="cm-search"
        placeholder="Search every capability"
        aria-label="Search capabilities"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
      />
      <span className="spacer" aria-hidden="true" />
      <button
        type="button"
        data-export-explanation
        onClick={onExportClick}
        className="btn ghost sm"
      >
        Export explanation
      </button>
    </div>
  );
}

/** The grouped pane wired to the filter state (the honest copy + the group cards). */
function CapabilityPane({
  filters,
}: {
  readonly filters: ReturnType<typeof useCapabilityFilters>;
}): React.JSX.Element {
  const { searching, pool } = filters;
  const copy = paneCopy({
    searching,
    query: filters.query.trim(),
    shown: pool.length,
    area: filters.area,
    bucket: filters.bucket,
  });
  return (
    <CapabilityGroups
      title={copy.title}
      note={copy.note}
      mode={filters.mode}
      onMode={filters.setMode}
      groups={buildGroups(pool, filters.mode)}
      openId={filters.openId}
      onToggleRow={filters.toggleRow}
      more={filters.more}
      onMore={filters.toggleMore}
      emptyTitle={copy.emptyTitle}
      emptyNote={
        searching
          ? 'Try a different word, or clear the filters.'
          : 'Clear the filter to see everything in this area.'
      }
      onClearFilter={searching ? null : () => filters.setBucket('all')}
    />
  );
}

/** The loaded body — the header row (search + export), the ledger, the rail, and the grouped pane. */
function CapabilityMapBody({
  view,
  onExport,
}: {
  readonly view: CapabilityMapProjection;
  readonly onExport: (artifact: ScopedExplanationArtifact) => void;
}): React.JSX.Element {
  const filters = useCapabilityFilters(view.capabilities);
  const onExportClick = useCallback(() => {
    // A read-only composition of the SAME rendered projection — no new data, no write (G-154).
    onExport(composeScopedExplanation(view.capabilities));
  }, [onExport, view.capabilities]);

  return (
    <div data-capability-map>
      <CapabilityMapHeader
        query={filters.query}
        onQuery={filters.setQuery}
        onExportClick={onExportClick}
      />
      <CapabilityLedger
        caps={view.capabilities}
        bucket={filters.bucket}
        onBucket={filters.setBucket}
      />
      <div className="cm-cols">
        <CapabilityAreaRail
          areas={areaAggregates(filters.railPool)}
          allTotal={filters.railPool.length}
          allMix={bucketCounts(filters.railPool)}
          selected={filters.area}
          onSelect={filters.setArea}
        />
        <CapabilityPane filters={filters} />
      </div>
    </div>
  );
}
